import React from 'react';
import { Check, FileText, Brain, Edit3, ClipboardList, Mail } from 'lucide-react';
import { WorkflowStep } from '../../../../types/workflow.types';

interface WorkflowProgressBarProps {
  currentStep: number;
  steps?: WorkflowStep[];
  onStepClick: (step: WorkflowStep) => void;
  allowNavigation?: boolean;
}

const defaultSteps: Omit<WorkflowStep, 'status'>[] = [
  { id: 1, name: 'Contract Overview', shortName: 'Overview', icon: <FileText className="w-5 h-5" />, path: 'overview', description: 'Review contract details and upload documents' },
  { id: 2, name: 'AI Analysis', shortName: 'Analysis', icon: <Brain className="w-5 h-5" />, path: 'analysis', description: 'AI-powered contract analysis and risk assessment' },
  { id: 3, name: 'Clause Review', shortName: 'Review', icon: <Edit3 className="w-5 h-5" />, path: 'clauses', description: 'Review and edit individual clauses' },
  { id: 4, name: 'Summary', shortName: 'Summary', icon: <ClipboardList className="w-5 h-5" />, path: 'summary', description: 'Review changes and select items for negotiation' },
  { id: 5, name: 'Negotiation', shortName: 'Negotiate', icon: <Mail className="w-5 h-5" />, path: 'negotiation', description: 'Generate negotiation emails' },
];

export const WorkflowProgressBar: React.FC<WorkflowProgressBarProps> = ({
  currentStep,
  steps,
  onStepClick,
  allowNavigation = true
}) => {
  // Apply status to steps based on currentStep
  const stepsWithStatus: WorkflowStep[] = (steps || defaultSteps.map(s => ({ ...s, status: 'upcoming' as const }))).map((step, index) => ({
    ...step,
    status: index + 1 < currentStep ? 'completed' : index + 1 === currentStep ? 'current' : 'upcoming'
  }));

  const getStepStyles = (status: WorkflowStep['status']) => {
    switch (status) {
      case 'completed':
        return {
          circle: 'bg-success-500 dark:bg-success-400 text-white',
          line: 'bg-success-500 dark:bg-success-400',
          text: 'text-success-600 dark:text-success-400'
        };
      case 'current':
        return {
          circle: 'bg-primary-600 dark:bg-accent-500 text-white ring-4 ring-primary-200 dark:ring-accent-500/30',
          line: 'bg-primary-600 dark:bg-accent-500',
          text: 'text-primary-600 dark:text-accent-400 font-semibold'
        };
      default:
        return {
          circle: 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
          line: 'bg-gray-200 dark:bg-gray-700',
          text: 'text-gray-500 dark:text-gray-400'
        };
    }
  };

  return (
    <div className="w-full py-6 px-4">
      <div className="flex items-center justify-between max-w-4xl mx-auto">
        {stepsWithStatus.map((step, index) => {
          const styles = getStepStyles(step.status);
          const isClickable = allowNavigation && step.status !== 'upcoming';

          return (
            <React.Fragment key={step.id}>
              {/* Step indicator */}
              <button
                onClick={() => isClickable && onStepClick(step)}
                disabled={!isClickable}
                className={`flex flex-col items-center group relative ${
                  isClickable ? 'cursor-pointer' : 'cursor-default'
                }`}
                title={step.description}
              >
                {/* Circle with icon */}
                <div className={`
                  w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300
                  ${styles.circle}
                  ${isClickable ? 'hover:scale-110 hover:shadow-lg' : ''}
                `}>
                  {step.status === 'completed' ? (
                    <Check className="w-6 h-6" />
                  ) : (
                    step.icon
                  )}
                </div>

                {/* Step name - visible on desktop */}
                <span className={`
                  mt-2 text-sm font-medium hidden sm:block
                  ${styles.text}
                `}>
                  {step.shortName}
                </span>

                {/* Step number - visible on mobile */}
                <span className={`
                  mt-2 text-xs font-medium sm:hidden
                  ${styles.text}
                `}>
                  {step.id}
                </span>

                {/* Full name tooltip on hover */}
                <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-20">
                  <span className="text-xs bg-gray-800 dark:bg-gray-700 text-white px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg">
                    {step.name}
                  </span>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 dark:bg-gray-700 rotate-45" />
                </div>
              </button>

              {/* Connector line */}
              {index < stepsWithStatus.length - 1 && (
                <div className={`
                  flex-1 h-1 mx-2 sm:mx-4 rounded-full transition-colors duration-300
                  ${stepsWithStatus[index + 1].status !== 'upcoming'
                    ? getStepStyles(stepsWithStatus[index + 1].status).line
                    : 'bg-gray-200 dark:bg-gray-700'}
                `} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};
